// SupportCrewSystem.js - Ground support staff: mechanics, fleet operators, engineers
// These are NOT flight crew - they maintain aircraft and operations
// Each has RPG-style stats that affect aircraft reliability and costs

// ============================================
// SUPPORT CREW TYPES
// ============================================

export const SUPPORT_ROLES = {
  MECHANIC: {
    role: 'mechanic',
    title: 'Aircraft Mechanic',
    description: 'Performs line maintenance, inspections, and repairs. Better mechanics mean faster turnarounds and fewer AOG events.',
    baseStats: {
      skill: [40, 100],           // Repair quality
      speed: [40, 100],           // How fast they work
      reliability: [50, 100],     // Show up to work
    },
    salaryRange: [4500, 12000],   // $4.5K - $12K/month base
    certifications: ['A&P', 'IA', 'Avionics', 'Composites', 'Engine'],
  },

  FLEET_OPERATOR: {
    role: 'fleet_operator',
    title: 'Fleet Operator',
    description: 'Manages aircraft positioning, ground movements, and ramp operations. Better operators reduce taxi times and gate conflicts.',
    baseStats: {
      efficiency: [40, 100],      // Resource optimization
      coordination: [40, 100],    // Multi-aircraft handling
      problemSolving: [40, 100],  // Handles disruptions
    },
    salaryRange: [5000, 14000],   // $5K - $14K/month base
    certifications: ['Ramp', 'Dispatch', 'Ground Control', 'Deicing'],
  },

  ENGINEER: {
    role: 'engineer',
    title: 'Maintenance Engineer',
    description: 'Plans maintenance schedules, manages heavy checks, and oversees technical operations. Better engineers reduce maintenance costs.',
    baseStats: {
      technical: [50, 100],       // Technical knowledge
      planning: [50, 100],        // Schedule optimization
      management: [40, 100],      // Team coordination
    },
    salaryRange: [7000, 18000],   // $7K - $18K/month base
    certifications: ['MRO', 'Structures', 'Powerplant', 'Systems', 'NDT'],
  },
};

// ============================================
// SUPPORT CREW GENERATION
// ============================================

const FIRST_NAMES = [
  'Mike', 'Dave', 'Tony', 'Steve', 'Frank', 'Joe', 'Rick', 'Tom', 'Bill', 'Dan',
  'Maria', 'Rosa', 'Linda', 'Karen', 'Susan', 'Lisa', 'Nancy', 'Betty', 'Helen', 'Sandra',
  'Carlos', 'Luis', 'Juan', 'Jorge', 'Pedro', 'Raj', 'Vijay', 'Chen', 'Wei', 'Kim'
];

const LAST_NAMES = [
  'Martinez', 'Johnson', 'Williams', 'Garcia', 'Brown', 'Davis', 'Miller', 'Wilson',
  'Anderson', 'Taylor', 'Thomas', 'Jackson', 'White', 'Harris', 'Clark', 'Lewis',
  'Lee', 'Walker', 'Hall', 'Allen', 'Young', 'King', 'Wright', 'Scott', 'Green'
];

export function generateSupportCrewMarket(count = 15) {
  const market = [];
  
  // Generate mix of roles
  const roles = Object.keys(SUPPORT_ROLES);
  for (let i = 0; i < count; i++) {
    const role = roles[i % roles.length];
    market.push(generateSupportCrew(role));
  }
  
  return market.sort((a, b) => a.salary - b.salary);
}

export function generateSupportCrew(roleType, overrides = {}) {
  const roleConfig = SUPPORT_ROLES[roleType.toUpperCase()];
  if (!roleConfig) return null;

  // Determine tier based on stat rolls
  const stats = generateSupportStats(roleConfig);
  const avgStat = Object.values(stats).reduce((a, b) => a + b, 0) / Object.values(stats).length;
  
  let tier;
  if (avgStat >= 85) tier = 'expert';
  else if (avgStat >= 70) tier = 'veteran';
  else if (avgStat >= 55) tier = 'journeyman';
  else tier = 'apprentice';

  const certifications = generateCertifications(roleConfig, tier);
  const salary = calculateSupportSalary(roleConfig, stats, certifications);
  const traits = generateSupportTraits(tier);

  return {
    id: `support-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name: generateName(),
    role: roleConfig.role,
    title: roleConfig.title,
    tier,
    
    // Stats
    stats,
    
    // Experience
    yearsExperience: tier === 'expert' ? randomInRange(15, 30) :
                     tier === 'veteran' ? randomInRange(8, 15) :
                     tier === 'journeyman' ? randomInRange(3, 8) :
                     randomInRange(0, 3),
    
    // Qualifications
    certifications,
    
    // Contract
    salary,
    signingBonus: Math.round(salary * (0.3 + Math.random() * 0.5)),
    
    // Traits
    traits,
    
    // State
    available: true,
    assignedTo: null, // Can be assigned to specific aircraft or base
    
    ...overrides,
  };
}

function generateSupportStats(roleConfig) {
  const stats = {};
  Object.entries(roleConfig.baseStats).forEach(([stat, range]) => {
    stats[stat] = randomInRange(range[0], range[1]);
  });
  return stats;
}

function generateCertifications(roleConfig, tier) {
  const available = roleConfig.certifications;
  const numCerts = tier === 'expert' ? randomInRange(3, available.length) :
                   tier === 'veteran' ? randomInRange(2, 4) :
                   tier === 'journeyman' ? randomInRange(1, 3) :
                   randomInRange(1, 2);
  
  const certs = [];
  const shuffled = [...available].sort(() => Math.random() - 0.5);
  for (let i = 0; i < Math.min(numCerts, shuffled.length); i++) {
    certs.push(shuffled[i]);
  }
  return certs;
}

function calculateSupportSalary(roleConfig, stats, certifications) {
  const [minSalary, maxSalary] = roleConfig.salaryRange;
  const avgStat = Object.values(stats).reduce((a, b) => a + b, 0) / Object.values(stats).length;
  
  // Base salary scales with stats
  const statMultiplier = avgStat / 100;
  let salary = minSalary + (maxSalary - minSalary) * statMultiplier;
  
  // Certification bonuses
  salary += certifications.length * 500;
  
  // Convert to cents and add variance
  salary = Math.round(salary * 100 * (0.9 + Math.random() * 0.2));
  
  return salary;
}

function generateSupportTraits(tier) {
  const positiveTraits = [
    'detail_oriented',
    'team_leader',
    'fast_learner',
    'night_owl',        // Doesn't mind overnight shifts
    'by_the_book',      // Never cuts corners (good for safety)
    'creative_solutions',
    'mentor',
    'calm_under_pressure',
  ];
  
  const negativeTraits = [
    'slow_but_thorough',
    'overtime_averse',
    'conflict_prone',
    'shortcuts',        // Cuts corners (bad for safety)
    'weather_sensitive', // Calls in during bad weather
  ];
  
  const traits = [];
  const numPositive = tier === 'expert' ? 2 : tier === 'veteran' ? 1 : Math.random() > 0.6 ? 1 : 0;
  const numNegative = tier === 'apprentice' ? (Math.random() > 0.5 ? 1 : 0) : 0;
  
  for (let i = 0; i < numPositive; i++) {
    const trait = positiveTraits[Math.floor(Math.random() * positiveTraits.length)];
    if (!traits.includes(trait)) traits.push(trait);
  }
  
  for (let i = 0; i < numNegative; i++) {
    const trait = negativeTraits[Math.floor(Math.random() * negativeTraits.length)];
    if (!traits.includes(trait)) traits.push(trait);
  }
  
  return traits;
}

function generateName() {
  return `${FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)]} ${LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)]}`;
}

function randomInRange(min, max) {
  return Math.floor(min + Math.random() * (max - min + 1));
}

// ============================================
// SUPPORT CREW STATE
// ============================================

export function createSupportCrewState() {
  return {
    roster: {
      mechanics: [],
      fleet_operators: [],
      engineers: [],
    },
    market: generateSupportCrewMarket(15),
    totalMonthlySalary: 0,
  };
}

export function hireSupportCrew(state, crewId, cash) {
  const crew = state.market.find(c => c.id === crewId);
  if (!crew) {
    return { success: false, error: 'Crew member not found' };
  }
  
  const totalCost = crew.salary + crew.signingBonus;
  if (cash < totalCost) {
    return { success: false, error: 'Insufficient funds' };
  }
  
  // Add to appropriate roster
  const rosterKey = crew.role === 'mechanic' ? 'mechanics' :
                    crew.role === 'fleet_operator' ? 'fleet_operators' :
                    'engineers';
  
  const newRoster = {
    ...state.roster,
    [rosterKey]: [...state.roster[rosterKey], { ...crew, hiredAt: Date.now() }],
  };
  
  const newState = {
    ...state,
    roster: newRoster,
    market: state.market.filter(c => c.id !== crewId),
    totalMonthlySalary: state.totalMonthlySalary + crew.salary,
  };
  
  return {
    success: true,
    state: newState,
    cost: totalCost,
    message: `${crew.name} has joined as ${crew.title}.`,
  };
}

export function fireSupportCrew(state, crewId) {
  let crew = null;
  let rosterKey = null;
  
  // Find in rosters
  for (const [key, roster] of Object.entries(state.roster)) {
    const found = roster.find(c => c.id === crewId);
    if (found) {
      crew = found;
      rosterKey = key;
      break;
    }
  }
  
  if (!crew) {
    return { success: false, error: 'Crew member not found' };
  }
  
  const severance = crew.salary * 2; // 2 months severance
  
  const newRoster = {
    ...state.roster,
    [rosterKey]: state.roster[rosterKey].filter(c => c.id !== crewId),
  };
  
  const newState = {
    ...state,
    roster: newRoster,
    totalMonthlySalary: state.totalMonthlySalary - crew.salary,
  };
  
  return {
    success: true,
    state: newState,
    cost: severance,
    message: `${crew.name} has been let go. Severance: $${formatMoney(severance)}`,
  };
}

export function refreshSupportMarket(state) {
  const newCandidates = generateSupportCrewMarket(5);
  const remaining = state.market.filter(() => Math.random() > 0.3);
  
  return {
    ...state,
    market: [...remaining, ...newCandidates].slice(0, 18),
  };
}

// ============================================
// EFFECT CALCULATIONS
// ============================================

export function getMechanicEfficiency(supportState) {
  const mechanics = supportState?.roster?.mechanics || [];
  if (mechanics.length === 0) return 0.5; // Baseline without dedicated mechanics
  
  const avgSkill = mechanics.reduce((sum, m) => sum + m.stats.skill, 0) / mechanics.length;
  const avgSpeed = mechanics.reduce((sum, m) => sum + m.stats.speed, 0) / mechanics.length;
  
  return (avgSkill + avgSpeed) / 200; // 0.4 to 1.0
}

export function getMaintenanceCostModifier(supportState) {
  // Better mechanics + engineers = lower maintenance costs
  const mechanics = supportState?.roster?.mechanics || [];
  const engineers = supportState?.roster?.engineers || [];
  
  if (mechanics.length === 0 && engineers.length === 0) return 1.2; // 20% penalty
  
  let modifier = 1.0;
  
  if (mechanics.length > 0) {
    const avgSkill = mechanics.reduce((sum, m) => sum + m.stats.skill, 0) / mechanics.length;
    modifier -= (avgSkill - 50) / 500; // Up to -10% for skill 100
  }
  
  if (engineers.length > 0) {
    const avgPlanning = engineers.reduce((sum, e) => sum + e.stats.planning, 0) / engineers.length;
    modifier -= (avgPlanning - 50) / 400; // Up to -12.5% for planning 100
  }
  
  return Math.max(0.75, modifier); // Cap at 25% reduction
}

export function getTurnaroundTimeModifier(supportState) {
  const mechanics = supportState?.roster?.mechanics || [];
  const operators = supportState?.roster?.fleet_operators || [];
  
  if (mechanics.length === 0 && operators.length === 0) return 1.3; // 30% slower
  
  let modifier = 1.0;
  
  if (mechanics.length > 0) {
    const avgSpeed = mechanics.reduce((sum, m) => sum + m.stats.speed, 0) / mechanics.length;
    modifier -= (avgSpeed - 50) / 300; // Up to -16% for speed 100
  }
  
  if (operators.length > 0) {
    const avgEfficiency = operators.reduce((sum, o) => sum + o.stats.efficiency, 0) / operators.length;
    modifier -= (avgEfficiency - 50) / 400; // Up to -12.5%
  }
  
  return Math.max(0.7, modifier); // Cap at 30% reduction
}

export function getAOGPreventionBonus(supportState) {
  // Better support crew = fewer AOG events
  const mechanics = supportState?.roster?.mechanics || [];
  const engineers = supportState?.roster?.engineers || [];
  
  let bonus = 0;
  
  // Each expert mechanic adds prevention
  mechanics.forEach(m => {
    if (m.tier === 'expert') bonus += 0.05;
    else if (m.tier === 'veteran') bonus += 0.03;
    else if (m.tier === 'journeyman') bonus += 0.01;
  });
  
  // Engineers with NDT certification
  engineers.forEach(e => {
    if (e.certifications.includes('NDT')) bonus += 0.05;
  });
  
  return Math.min(0.40, bonus); // Cap at 40% additional prevention
}

export function getDisruptionHandlingBonus(supportState) {
  const operators = supportState?.roster?.fleet_operators || [];
  
  if (operators.length === 0) return 0;
  
  const avgProblemSolving = operators.reduce((sum, o) => sum + o.stats.problemSolving, 0) / operators.length;
  return (avgProblemSolving - 50) / 200; // -0.25 to +0.25 based on skill
}

// ============================================
// UTILITY
// ============================================

function formatMoney(cents) {
  if (cents >= 100000000) return `${(cents / 100000000).toFixed(1)}M`;
  if (cents >= 100000) return `${(cents / 100000).toFixed(0)}K`;
  return (cents / 100).toLocaleString();
}

// ============================================
// EXPORTS
// ============================================

export default {
  SUPPORT_ROLES,
  generateSupportCrewMarket,
  generateSupportCrew,
  createSupportCrewState,
  hireSupportCrew,
  fireSupportCrew,
  refreshSupportMarket,
  getMechanicEfficiency,
  getMaintenanceCostModifier,
  getTurnaroundTimeModifier,
  getAOGPreventionBonus,
  getDisruptionHandlingBonus,
};
